import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import {
    Box, Typography, Grid, Stack, Button, Chip, FormControl, Select, MenuItem,
    Table, TableBody, TableContainer, TableHead, TableRow, Paper, Pagination
} from '@mui/material';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import AddIcon from '@mui/icons-material/Add';
import { setMainHeading } from '../../../../redux/modules/mainHeading/slice';
import { getQCResultsRequest } from '../../../../redux/modules/laboratory/labQC/slice';
import { useNavigate, useLocation } from 'react-router-dom';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: { backgroundColor: theme.palette.primary.main, color: theme.palette.common.white },
    [`&.${tableCellClasses.body}`]: { fontSize: 14 },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': { backgroundColor: theme.palette.action.hover },
    '&:last-child td, &:last-child th': { border: 0 },
}));

let limit = 20;

export default function LabQCDashboard() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const basePath = location.pathname.split('/laboratory')[0];
    const { getResultsRes } = useSelector(state => state.labQC);
    const [statusFilter, setStatusFilter] = useState('');
    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
        dispatch(setMainHeading("Quality Control"));
    }, [dispatch]);

    useEffect(() => {
        dispatch(getQCResultsRequest({ page: currentPage, limit, status: statusFilter }));
    }, [dispatch, currentPage, statusFilter]);

    const pageCount = getResultsRes?.dataCount > limit ? Math.ceil(getResultsRes.dataCount / limit) : 0;

    const statusColors = {
        accepted: 'success', rejected: 'error', pending_review: 'warning'
    };

    return (
        <>
            <Stack mb={3} mt={4} direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between">
                <Stack direction="row" spacing={2}>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate(`${basePath}/laboratory/qc/entry`)}>
                        Enter QC Result
                    </Button>
                    <Button variant="outlined" onClick={() => navigate(`${basePath}/laboratory/qc/materials`)}>
                        QC Materials
                    </Button>
                </Stack>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <Select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }} displayEmpty>
                        <MenuItem value="">All Status</MenuItem>
                        <MenuItem value="accepted">Accepted</MenuItem>
                        <MenuItem value="rejected">Rejected</MenuItem>
                        <MenuItem value="pending_review">Pending Review</MenuItem>
                    </Select>
                </FormControl>
            </Stack>

            <Box sx={{ overflowX: { xs: 'auto', md: 'visible' } }}>
                <TableContainer component={Paper} className="tableShadow">
                    <Table sx={{ minWidth: { xs: 500, md: 800 } }} className="commonTable">
                        <TableHead>
                            <TableRow>
                                <StyledTableCell>Date/Time</StyledTableCell>
                                <StyledTableCell>QC Material</StyledTableCell>
                                <StyledTableCell>Test</StyledTableCell>
                                <StyledTableCell align="center">Value</StyledTableCell>
                                <StyledTableCell align="center">z-Score</StyledTableCell>
                                <StyledTableCell>Status</StyledTableCell>
                                <StyledTableCell>Violations</StyledTableCell>
                                <StyledTableCell>Performed By</StyledTableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {getResultsRes?.data?.length > 0 ? getResultsRes.data.map(r => (
                                <StyledTableRow key={r._id}>
                                    <StyledTableCell sx={{ whiteSpace: 'nowrap' }}>
                                        {r.performedAt ? new Date(r.performedAt).toLocaleString() : '-'}
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        {r.qcMaterialId?.name || '-'}
                                        {r.qcMaterialId?.level && (
                                            <Typography variant="caption" display="block" color="text.secondary">
                                                {r.qcMaterialId.level} | Lot: {r.qcMaterialId.lotNumber}
                                            </Typography>
                                        )}
                                    </StyledTableCell>
                                    <StyledTableCell>{r.testId?.testCode || '-'}</StyledTableCell>
                                    <StyledTableCell align="center">
                                        <Typography sx={{ fontWeight: r.hasViolation ? 700 : 400, color: r.hasViolation ? 'error.main' : 'inherit' }}>
                                            {r.value} {r.unit || ''}
                                        </Typography>
                                    </StyledTableCell>
                                    <StyledTableCell align="center">
                                        <Chip
                                            label={r.zScore?.toFixed(2) || '0'}
                                            size="small"
                                            color={Math.abs(r.zScore) > 3 ? 'error' : Math.abs(r.zScore) > 2 ? 'warning' : 'default'}
                                            variant="outlined"
                                        />
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        <Chip label={r.status?.replace('_', ' ')} size="small" color={statusColors[r.status] || 'default'} sx={{ textTransform: 'capitalize' }} />
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        {r.westgardViolations?.length > 0 ? (
                                            <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                                {r.westgardViolations.map((v, i) => (
                                                    <Chip key={i} label={v.rule.replace('_', '-')} size="small" color="error" variant="outlined" />
                                                ))}
                                            </Stack>
                                        ) : '-'}
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        {r.performedBy ? `${r.performedBy.firstName || ''} ${r.performedBy.lastName || ''}`.trim() : '-'}
                                    </StyledTableCell>
                                </StyledTableRow>
                            )) : (
                                <StyledTableRow>
                                    <StyledTableCell colSpan={8}><Typography>No QC results found</Typography></StyledTableCell>
                                </StyledTableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
            {pageCount > 0 && (
                <Stack direction="row" justifyContent="center" mt={2}>
                    <Pagination count={pageCount} page={currentPage} onChange={(e, v) => setCurrentPage(v)} />
                </Stack>
            )}
        </>
    );
}
